import { Grid, IconButton } from "@mui/material";
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useAuthContext } from "../../../../contexts/AuthContext";
import DefaultConstants from "../../../../data/Constants";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from "../../../../components/ServiceOrder/Filters";
import useDrawer from "../../../../hooks/useDrawer";
import { useCallback, useEffect, useMemo } from "react";
import { useServiceOrderContext } from "../../../../contexts/ServiceOrderContext";
import Tooltip from "../../../../components/Tooltip";
import { FilterAlt, HomeRepairService, Refresh } from "@mui/icons-material";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import { ServicePriorityData, ServiceStatusData } from "../../../../data/Enums";
import DataCard from "../../../../components/DataCard";
import ChartCard from "../../../../components/ChartCard";
import { Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from "recharts";
import { ReportsAlias, ServiceReports } from "../../../../data/Reports";
import IReport from "../../../../interfaces/IReport";
import { IServiceOrdersReportFilters } from "../Reports/ServiceOrders/Filters";
import { useReportPreviewContext } from "../../../../contexts/ReportPreviewContext";


const Dashboard = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();
    const { enterprise, hasPermission } = useAuthContext();
    const {
        selectedStatus,
        selectedPriority,
        selectedDepartment, 
        filteredServiceOrders, 
        startDateOperator, 
        startDate, 
        estimatedCompletionDateOperator, 
        estimatedCompletionDate,
        handleRefreshServiceOrders 
    } = useServiceOrderContext();
    const { handleOpen } = useReportPreviewContext();

    const filterDrawer = useDrawer();


    useEffect(() => {
        handleRefresh();
        
        const milliseconds = environment.serviceOrder.dataRefreshTimeInSeconds * 1000;
        const interval = setInterval(() => {
            handleRefresh();
        }, milliseconds);
            
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, []);
    

    const handleRefresh = useCallback(() => {
        handleRefreshServiceOrders();
        // eslint-disable-next-line
    }, []);


    const handleOpenReportPreview = useCallback((key?: string, value?: any) => {
        if (!hasPermission(ReportsAlias.Service.ServiceOrders)) {
            return;
        }

        const report = ServiceReports.find(r => r.alias === ReportsAlias.Service.ServiceOrders) as IReport;
        let filters: IServiceOrdersReportFilters = {
            enterprise: enterprise,
            status: selectedStatus,
            priority: selectedPriority,
            department: enterprise?.departments.filter(d => selectedDepartment.includes(+d.id)) ?? [],
            responsible: [],
            room: [],
            startDateOperator: startDateOperator,
            startDate: startDate,
            estimatedCompletionDateOperator: estimatedCompletionDateOperator,
            estimatedCompletionDate: estimatedCompletionDate
        };

        if (key !== undefined && value !== undefined) {
            const keyFilter = key as keyof(IServiceOrdersReportFilters);

            filters = {
                ...filters,
                [keyFilter]: [
                    ...filters[keyFilter] as any[],
                    value
                ]
            };
        }

        handleOpen(report, filters);
        // eslint-disable-next-line
    }, [enterprise, selectedStatus, selectedPriority, selectedDepartment, startDateOperator, startDate, estimatedCompletionDateOperator, estimatedCompletionDate]);


    const dashStatusData = useMemo(() => {
        return Object.values(ServiceStatusData).map(s => {
            const name = translate(s.name);
            const value = filteredServiceOrders.filter(r => +(r.statusId ?? 0) === s.key).length;
            const percentage = filteredServiceOrders.length === 0 ? 0 : value / filteredServiceOrders.length * 100;

            return {
                chartData: {
                    name: name,
                    value: value,
                    fill: s.color
                },
                dataCard: <DataCard
                    variant='dashboard'
                    variantProps={{
                        dashboard: {
                            title: name,
                            value: value.toString(),
                            valueSulfix: translate('requests'),
                            percentage: percentage
                        }
                    }}
                    color={s.color}
                    Icon={s.Icon}
                    key={s.key}
                    onClick={() => handleOpenReportPreview('status', s.key)}
                />
            };
        });

        // eslint-disable-next-line
    }, [ServiceStatusData, filteredServiceOrders, translate]);


    const dashPriorityData = useMemo(() => {
        return Object.values(ServicePriorityData).map((s, index) => {
            const name = translate(s.name);
            const value = filteredServiceOrders.filter(r => +(r.priorityId ?? 0) === s.key).length;
            const percentage = filteredServiceOrders.length === 0 ? 0 : value / filteredServiceOrders.length * 100;

            return {
                chartData: {
                    name: name,
                    value: value,
                    fill: s.color
                },
                dataCard: <DataCard
                    variant='dashboard'
                    variantProps={{
                        dashboard: {
                            title: name,
                            value: value.toString(),
                            valueSulfix: translate('requests'),
                            percentage: percentage
                        }
                    }}
                    color={s.color}
                    Icon={s.Icon}
                    key={s.key}
                    onClick={() => handleOpenReportPreview('priority', s.key)}
                />
            };
        });
        
        // eslint-disable-next-line
    }, [ServicePriorityData, filteredServiceOrders, translate]);


    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle 
                        name={translate(linkProps.name)} 
                        description={enterprise?.name}
                        applyTranslation={false}
                    />
                </Grid>

                <Grid item className='grid-tools'>
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filterDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container 
                className='grid-content overflow-enabled justifycontent-flexstart aligncontent-flexstart' 
                rowSpacing={DefaultConstants.gridRowSpacing} 
                columnSpacing={DefaultConstants.gridColumnSpacing}
            >
                <Grid item lg={2.4} md={4} sm={12} xs={12}>
                    <DataCard
                        variant='dashboard' 
                        variantProps={{
                            dashboard: {
                                title: translate('total'),
                                value: filteredServiceOrders.length.toString(),
                                valueSulfix: translate('requests'),
                                percentage: 100
                            }
                        }}
                        Icon={HomeRepairService}
                        className='color-primary'
                        onClick={() => handleOpenReportPreview()}
                    />
                </Grid>

                {
                    hasPermission(`${linkProps.alias[0]}.Status`) && <>
                        <Grid item xs={12} />

                        {
                            dashStatusData.map((status, index) => {
                                const gridMdSize = 12 / dashStatusData.length;
                                return <Grid item lg={gridMdSize} md={4} sm={6} xs={12} key={index}>
                                    {status.dataCard}
                                </Grid>;
                            })
                        }
                    </>
                }

                {
                    hasPermission(`${linkProps.alias[0]}.Priority`) && <>
                        <Grid item xs={12} />

                        {
                            dashPriorityData.map((status, index) => {
                                const gridMdSize = 12 / dashPriorityData.length;
                                return <Grid item lg={gridMdSize} md={4} sm={6} xs={12} key={index}>
                                    {status.dataCard}
                                </Grid>;
                            })
                        }
                    </>
                }

                <Grid item xs={12} />

                {
                    hasPermission(`${linkProps.alias[0]}.Status`) && (
                        <Grid item sm={6} xs={12}>
                            <ChartCard
                                title={translate('status')}
                                chart={
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                dataKey="value"
                                                isAnimationActive={true}
                                                paddingAngle={2}
                                                innerRadius={50}
                                                outerRadius={100}
                                                data={dashStatusData.map(status => status.chartData)}
                                            />
                                            <RechartsTooltip />
                                            <Legend align='left' iconSize={12} height={40} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                }
                            />
                        </Grid>
                    )
                }

                {
                    hasPermission(`${linkProps.alias[0]}.Priority`) && (
                        <Grid item sm={6} xs={12}>
                            <ChartCard
                                title={translate('priority')}
                                chart={
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                dataKey="value"
                                                isAnimationActive={true}
                                                paddingAngle={2}
                                                innerRadius={50}
                                                outerRadius={100}
                                                data={dashPriorityData.map(status => status.chartData)}
                                            />
                                            <RechartsTooltip />
                                            <Legend align='left' iconSize={12} height={40} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                }
                            />
                        </Grid>
                    )
                }
            </Grid>

            <Filters
                open={filterDrawer.open}
                handleClose={() => filterDrawer.handleClose()}
            />
        </>
    );
}

export default Dashboard;